import {
	Stack,
	Heading,
	Text,
	Box,
	Button,
	Inline,
	Icons,
	Card,
	CardBody,
	Col,
	Component,
	DeferRender,
	Grid,
	Spinner,
	useBreakpoints,
	useId,
} from "@sembark-travel/ui-base"
import { useDialog, Dialog } from "@sembark-travel/ui-dialog"
import {
	utcTimestampToLocalDate,
	clone,
	isBefore,
	addUnit,
	formatDate,
	getDiff,
	parseDate,
	subtractUnit,
	startOf,
	endOf,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { useXHR } from "@sembark-travel/xhr"
import React, { useCallback, useMemo, useState } from "react"
import { $PropertyType, Required } from "utility-types"
import * as Validator from "yup"
import { CalculatePriceForm, ExtraServicesForm } from "../HotelPrices"
import { getGlobalNotesForHotelDestination, IHotel } from "../Hotels"
import {
	getEditableHotelExtrasFromBooking,
	getEditableHotelsFromBooking,
	getRoomStatusOnDate,
	IBooking,
	transformHotelExtrasToRequestDataForBooking,
	transformHotelsToRequestDataForBooking,
} from "./store"
import { ChildrenInputField, childrenToQueryParams } from "./../Tourists"
import { IQuoteHotelExtraParams, IQuoteHotelParams } from "./../Trips/store"
import {
	Form,
	TextInputField,
	withServerErrors,
	validateFormValues,
	SubmissionError,
	GetFieldValue,
	EmptyNumberValidator,
} from "@sembark-travel/ui-form"
import { TTripDestination } from "../TripDestinations"
import { TChildrenArray, TTourist, TTouristGroup } from "../Tourists"
import useSWR from "swr"
import {
	TBookingTouristUpdate,
	TouristAssignmentInputField,
} from "../Tourists/TouristAssignmentInputField"
import { showClosedServicesAlert } from "../TravelActivities"
interface EditDetailsProps {
	booking: IBooking
	startDate: Date
	endDate: Date
	onCancel?: () => void
	onSuccess?: () => void
	tripDestinations: Array<TTripDestination>
}

export function EditHotelBookingDetailsInDialog({
	level,
	minimal,
	children,
	...props
}: EditDetailsProps &
	Pick<React.ComponentProps<typeof Button>, "level"> & {
		minimal?: boolean
		children?: (props: { edit: () => void }) => React.ReactNode
	}) {
	const {
		booking: { can_modify, booking_stage },
	} = props
	const [isOpen, openDialog, closeDialog] = useDialog()
	if (
		!can_modify ||
		(booking_stage.state !== "initialized" &&
			booking_stage.state !== "booked" &&
			booking_stage.state !== "in_progress")
	)
		return null
	return (
		<>
			{children ? (
				children({ edit: openDialog })
			) : (
				<Button
					level={minimal ? "tertiary" : level}
					inline={minimal}
					onClick={openDialog}
				>
					{minimal ? <Icons.Pencil title="Edit Details" /> : "Edit Details"}
				</Button>
			)}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				fitContainer
				title={`Edit Booking Details for ${props.booking.hotel.name}`}
			>
				<Dialog.Body>
					<DeferRender>
						<EditDetails
							{...props}
							onCancel={() => {
								closeDialog()
								props.onCancel && props.onCancel()
							}}
							onSuccess={() => {
								closeDialog()
								props.onSuccess && props.onSuccess()
							}}
						/>
					</DeferRender>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function EditDetails({
	booking: initialBooking,
	startDate,
	endDate,
	onCancel,
	onSuccess,
	tripDestinations,
}: EditDetailsProps) {
	const xhr = useXHR()
	const refreshId = useId()

	const { data: booking } = useSWR<IBooking>(
		`/hotel-bookings/${initialBooking.id}?${refreshId}`,
		() =>
			xhr.get(`/hotel-bookings/${initialBooking.id}`).then((resp) => {
				const { data, meta } = resp.data

				return {
					...data,
					hotel: {
						...data.hotel,
						global_notes: meta.general_hotel_notes
							? getGlobalNotesForHotelDestination({
									generalNotes: meta.general_hotel_notes,
									hotelDestination: data.hotel.trip_destinations,
								})
							: [],
					},
				}
			}),
		{}
	)
	// just so hooks can verify, we are converting these to string
	const startDateISO = startDate.toISOString()
	const endDateISO = endDate.toISOString()
	const quoteHotels = useMemo(() => {
		if (!booking) return []
		const startDate = parseDate(startDateISO)
		const endDate = parseDate(endDateISO)
		return getEditableHotelsFromBooking(
			(booking.details || []).map(({ comments, ...details }) => {
				return {
					dates: getDatesFromInterval(
						utcTimestampToLocalDate(booking.checkin),
						utcTimestampToLocalDate(booking.checkout)
					),
					hotel: booking.hotel,
					comments: comments || "",
					...details,
				}
			}),
			startOf(clone(startDate), "day"),
			startOf(clone(startDate), "day"),
			getDiff(endOf(endDate, "day"), startOf(startDate, "day"), "days") + 1
		)
	}, [booking, startDateISO, endDateISO])
	const quoteHotelExtras = useMemo(() => {
		if (!booking) return []
		const startDate = parseDate(startDateISO)
		const endDate = parseDate(endDateISO)
		return getEditableHotelExtrasFromBooking(
			(booking.extras || []).map(({ comments, ...details }) => {
				return {
					hotel: booking.hotel,
					comments: comments || "",
					...details,
				}
			}),
			startOf(clone(startDate), "day"),
			startOf(clone(startDate), "day"),
			getDiff(endOf(endDate, "day"), startOf(startDate, "day"), "days") + 1
		)
	}, [booking, startDateISO, endDateISO])

	if (!booking) {
		return <Spinner padding="4" alignCenter />
	}

	return (
		<HotelBookingDetailsForm
			tripDestinations={tripDestinations}
			startDate={startDate}
			endDate={endDate}
			children={booking.children}
			noOfAdults={booking.no_of_adults}
			initialHotels={quoteHotels}
			initialHotelExtras={quoteHotelExtras}
			onCancel={onCancel}
			hotel={booking.hotel}
			tourists={booking.tourists}
			tourist={booking.tourist}
			members={booking.members}
			onSubmit={async (details, { children, noOfAdults }) => {
				return xhr
					.patch(`/hotel-bookings/${booking.id}`, {
						children: children,
						no_of_adults: noOfAdults,
						details: details.hotels,
						extras: details.extras,
						members: details.members,
						primary_tourist: details.primary_tourist,
						tourists: details.tourists,
					})
					.then(onSuccess)
			}}
			tripId={booking.trip_id}
		/>
	)
}

export function AddReplacementHotelForm({
	booking: initialBooking,
	startDate,
	endDate,
	...props
}: Omit<EditDetailsProps, "onSuccess"> &
	Pick<React.ComponentProps<typeof HotelBookingDetailsForm>, "onSubmit">) {
	const xhr = useXHR()

	const { data: booking } = useSWR<Required<IBooking, "trip">>(
		`/hotel-bookings/${initialBooking.id}`,
		() =>
			xhr
				.get(`/hotel-bookings/${initialBooking.id}`)
				.then((resp) => resp.data.data)
	)
	// just so hooks can verify, we are converting these to string
	const startDateISO = startDate.toISOString()
	const endDateISO = endDate.toISOString()
	const quoteHotels = useMemo(() => {
		if (!booking) return []
		const startDate = parseDate(startDateISO)
		const endDate = parseDate(endDateISO)
		return getEditableHotelsFromBooking(
			(booking.details || []).map(({ comments, ...details }) => {
				return {
					dates: getDatesFromInterval(
						localOrUtcTimestampToLocalDate(
							booking.checkin_local,
							booking.checkin
						),
						localOrUtcTimestampToLocalDate(
							booking.checkout_local,
							booking.checkout
						)
					),
					hotel: booking.hotel,
					comments: comments || "",
					...details,
				}
			}),
			clone(startDate),
			clone(startDate),
			getDiff(endDate, startDate, "days") + 1
		).map(({ hotel, ...otherDetails }) => ({
			hotel: undefined as never,
			...otherDetails,
		}))
	}, [booking, startDateISO, endDateISO])
	const quoteHotelExtras = useMemo(() => {
		if (!booking) return []
		const startDate = parseDate(startDateISO)
		const endDate = parseDate(endDateISO)
		return getEditableHotelExtrasFromBooking(
			(booking.extras || []).map(({ comments, ...details }) => {
				return {
					hotel: booking.hotel,
					comments: comments || "",
					...details,
				}
			}),
			clone(startDate),
			clone(startDate),
			getDiff(endDate, startDate, "days") + 1
		).map(({ hotel, ...otherDetails }) => ({
			hotel: undefined as never,
			...otherDetails,
		}))
	}, [booking, startDateISO, endDateISO])

	if (!booking) {
		return <Spinner padding="4" alignCenter />
	}
	return (
		<HotelBookingDetailsForm
			{...props}
			startDate={startDate}
			endDate={endDate}
			children={booking.children}
			noOfAdults={booking.no_of_adults}
			initialHotels={quoteHotels}
			initialHotelExtras={quoteHotelExtras}
			tripId={booking.trip_id}
		/>
	)
}

// const hoteDetailsFormValidationSchems = Validator.object().shape({
// 	noOfAdults: EmptyNumberValidator()
// 		.positive("Number of adults should be a positive integer")
// 		.integer("Number of adults should be a positive integer")
// 		.required("Number of adults field is required"),
// 	children: Validator.array().of(
// 		Validator.object().shape({
// 			count: EmptyNumberValidator()
// 				.positive("Number of children should be positive integer")
// 				.integer("Number of children should be positive integer"),
// 			age: EmptyNumberValidator().positive(
// 				"Child age should a positive number"
// 			),
// 		})
// 	),
// })

// const validate = validateFormValues(hoteDetailsFormValidationSchems)

export function HotelBookingDetailsForm({
	startDate,
	endDate,
	children,
	noOfAdults,
	onCancel,
	onSubmit,
	initialHotels,
	initialHotelExtras,
	hotel,
	tripDestinations,
	tourists = [],
	tripId,
	tourist,
	members,
}: {
	startDate: Date
	endDate: Date
	children: $PropertyType<IBooking, "children">
	noOfAdults: number
	onCancel?: () => void
	onSubmit: (
		data: {
			hotels: Array<
				ReturnType<typeof transformHotelsToRequestDataForBooking>[number] & {
					children: $PropertyType<IBooking, "children">
					no_of_adults: number
				}
			>
			extras: Array<
				ReturnType<
					typeof transformHotelExtrasToRequestDataForBooking
				>[number] & {
					children: $PropertyType<IBooking, "children">
					no_of_adults: number
				}
			>
			members?: Array<{
				type: string
				id: number
			}>
			primary_tourist?: number
			tourists:
				| {
						id: number
						remarks: string | null
				  }[]
				| undefined
		},
		basic: {
			noOfAdults: number
			children: $PropertyType<IBooking, "children">
		},
		details: {
			details: IQuoteHotelParams
			extras: IQuoteHotelExtraParams
		}
	) => Promise<void>
	hotel?: IHotel
	initialHotels?: ReturnType<typeof getEditableHotelsFromBooking>
	initialHotelExtras?: ReturnType<typeof getEditableHotelExtrasFromBooking>
	tripDestinations: Array<TTripDestination>
	tourists?: TTourist[]
	tourist?: TTourist
	members?: IBooking["members"]
	tripId: number
}) {
	const [details, setDetails] = useState<IQuoteHotelParams>([])
	const [extras, setExtras] = useState<IQuoteHotelExtraParams>([])
	const handleHotelChange = useCallback(
		(details: IQuoteHotelParams) => {
			const modifiedDetails = details.map((detail) => {
				return {
					...detail,
					date_wise_prices: detail.date_wise_prices.map((dwp) => {
						const status = getRoomStatusOnDate(
							{
								black_out_date_intervals:
									detail.room_type.black_out_date_intervals,
							},
							dwp.date
						)
						return {
							...dwp,
							room_status: status,
						}
					}),
				}
			})
			setDetails(modifiedDetails)
		},
		[setDetails]
	)
	const handleHotelExtrasChange = useCallback(
		(details: IQuoteHotelExtraParams) => {
			setExtras(details)
		},
		[setExtras]
	)
	const guestDetailsInitialValues = useMemo(() => {
		return {
			children: children,
			noOfAdults,
			all_tourists: tourists,
			members: (members || []).map((member) => ({
				member_type: member.member_type,
				member_id: member.member_id,
				id:
					member.member_type === "tourists"
						? `tourist-${member.member_id}`
						: `group-${member.member_id}`,
				member: member.member,
				name: member.member?.name,
			})),
			tourists: tourists?.map((t) => ({
				id: t.id,
				remarks: t.booking_tourist?.remarks ?? null,
			})),
			primary_contact: tourist ? tourist.id : undefined,
		}
	}, [children, noOfAdults, tourists, members, tourist])

	const validationSchema = validateFormValues(
		Validator.object().shape({
			noOfAdults: EmptyNumberValidator()
				.positive("Number of adults should be a positive integer")
				.integer("Number of adults should be a positive integer")
				.required("Number of adults field is required"),
			children: Validator.array().of(
				Validator.object().shape({
					count: EmptyNumberValidator()
						.positive("Number of children should be positive integer")
						.integer("Number of children should be positive integer"),
					age: EmptyNumberValidator().positive(
						"Child age should a positive number"
					),
				})
			),
			members: Validator.array().of(Validator.mixed()).optional(),
		})
	)
	const { xs, sm, md, lg, xl } = useBreakpoints()
	return (
		<Box>
			<CalculatePriceForm
				noOfAdults={noOfAdults}
				children={children}
				bookingFrom={formatDate(startDate, "YYYY-MM-DD HH:mm:ss")}
				bookingTo={formatDate(
					subtractUnit(endDate, 1, "day"),
					"YYYY-MM-DD HH:mm:ss"
				)}
				onChange={handleHotelChange as never}
				showBookedPrices
				disableHotels={Boolean(hotel)}
				tripDestinations={tripDestinations}
				initialValues={initialHotels ? { hotels: initialHotels } : undefined}
				canAddMoreOption={false}
			/>
			<br />
			<ExtraServicesForm
				bookingFrom={formatDate(startDate, "YYYY-MM-DD HH:mm:ss")}
				bookingTo={formatDate(
					subtractUnit(endDate, 1, "day"),
					"YYYY-MM-DD HH:mm:ss"
				)}
				tripDestinations={tripDestinations}
				onChange={handleHotelExtrasChange as never}
				showBookedPrices
				hotels={hotel ? [hotel] : undefined}
				initialValues={
					initialHotelExtras ? { hotel_extras: initialHotelExtras } : undefined
				}
			/>
			<br />
			<Form<typeof guestDetailsInitialValues>
				initialValues={guestDetailsInitialValues}
				validate={validationSchema}
				onSubmit={withServerErrors(async (values) => {
					if (details.length) {
						const closeHotelData: Record<string, Date[]> = {}
						const blacoutRoomsData: Record<string, Date[]> = {}
						details.forEach((detail) => {
							const lable = `${detail.hotel.name}-${detail.room_type.name}`
							detail.date_wise_prices.forEach(
								({ room_status, date, booked_price }) => {
									if (room_status === "stop_sale") {
										if (!closeHotelData[lable]) {
											closeHotelData[lable] = []
										}
										closeHotelData[lable].push(date)
									}
									if (room_status === "blackout" && booked_price === 0) {
										if (!blacoutRoomsData[lable]) {
											blacoutRoomsData[lable] = []
										}
										blacoutRoomsData[lable].push(date)
									}
								}
							)
						})
						if (Object.keys(closeHotelData).length > 0) {
							const alertMessage = showClosedServicesAlert(closeHotelData)
							const alertHeader =
								"The following hotel rooms are closed on the selected dates:\n"
							const alertFooter =
								"\nPlease adjust the booking details accordingly."
							throw new Error(alertHeader + alertMessage + alertFooter)
						}
						if (Object.keys(blacoutRoomsData).length > 0) {
							const alertHeader =
								"Some of the hotel rooms are blackout on given dates:\n"
							const alertMessage = showClosedServicesAlert(blacoutRoomsData)
							const alertFooter =
								"\nPlease update the rooms rate before bookings."
							throw new Error(alertHeader + alertMessage + alertFooter)
						}
					}

					const hotels = transformHotelsToRequestDataForBooking(details)
					const hotelExtras =
						transformHotelExtrasToRequestDataForBooking(extras)
					if (!hotels.length && !hotelExtras.length) {
						throw new Error("Please provide valid details for services")
					}
					const memberTouristId: number[] = values?.members
						? Array.from(
								new Set(
									values.members.flatMap((m) => {
										if (m.member_type === "tourists") {
											return [m.member_id]
										} else if (m.member_type === "tourist_groups") {
											const group = m.member as TTouristGroup
											return group.members.map((gm) => gm.tourist.id)
										}
										return []
									})
								)
							)
						: []
					await onSubmit(
						{
							hotels: hotels.map((data) => ({
								...data,
								no_of_adults: values.noOfAdults,
								children: values.children,
							})),
							extras: hotelExtras.map((data) => ({
								...data,
								given_price: data.price,
								no_of_adults: values.noOfAdults,
								children: values.children,
							})),
							members: (values.members || []).length
								? (values.members || []).map((m) => ({
										type: m.member_type,
										id: m.member_id,
									}))
								: undefined,
							primary_tourist: (values.members || []).length
								? values.primary_contact
								: undefined,
							tourists: values?.tourists
								? values.tourists
										.map((t) => ({
											id: t.id,
											remarks:
												t.remarks && t.remarks.trim() ? t.remarks.trim() : null,
										}))
										.filter((t) =>
											memberTouristId.find((mtId) => mtId === t.id)
										)
								: undefined,
						},
						{ noOfAdults: values.noOfAdults, children: values.children },
						{ details, extras }
					)
				})}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, submitting, form }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="4">
							<SubmissionError />
							<Box>
								<Grid paddingX={"0"}>
									<Col
										xs={12}
										md={6}
										paddingLeft={"0"}
										paddingRight={md || lg || xl ? "4" : "0"}
									>
										<Card>
											<CardBody bgColor="subtle">
												<Stack gap="2">
													<Heading fontSize="md" as="h4">
														Guest Details
													</Heading>
													<Text color="muted" fontSize="sm">
														if necessary or beneficial, you can edit guest
														details for this particular booking.
													</Text>
													<Component initialState={false}>
														{({ state, setState }) =>
															!state ? (
																<Inline gap="4" alignItems="center">
																	<Box>
																		<GetFieldValue<number> name="noOfAdults">
																			{({ value: noOfAdults }) => (
																				<>{noOfAdults} Adults</>
																			)}
																		</GetFieldValue>
																		<GetFieldValue<
																			(typeof guestDetailsInitialValues)["children"]
																		> name="children">
																			{({ value: children }) =>
																				children?.length ? (
																					<>
																						{" "}
																						with{" "}
																						{childrenToQueryParams(
																							children
																						)}{" "}
																						Children
																					</>
																				) : null
																			}
																		</GetFieldValue>
																	</Box>
																	<Button
																		onClick={() => setState(true)}
																		size="sm"
																		level="tertiary"
																		title="Edit Guest Details"
																		data-testid="hb_edit_guest_details"
																	>
																		<Icons.Pencil />
																	</Button>
																</Inline>
															) : (
																<Grid gap="8">
																	<Col xs="auto">
																		<TextInputField
																			label="No of Adults"
																			name="noOfAdults"
																			type="number"
																			min={1}
																			max={1000000}
																		/>
																	</Col>
																	<Col xs={12} sm="auto">
																		<ChildrenInputField
																			name="children"
																			maxAge={
																				tripDestinations.length
																					? Math.max(
																							...tripDestinations.map(
																								(d) => d.max_child_age || 12
																							)
																						)
																					: undefined
																			}
																		/>
																	</Col>
																	<Col xs={12} sm="auto" paddingTop="4">
																		<Button
																			onClick={() => {
																				form.initialize(
																					guestDetailsInitialValues
																				)
																				setState(false)
																			}}
																		>
																			Cancel
																		</Button>
																	</Col>
																</Grid>
															)
														}
													</Component>
												</Stack>
											</CardBody>
										</Card>
									</Col>

									{/* --- Tourist Assignment Flow for this booking --- */}
									<Col
										xs={12}
										md={6}
										marginTop={xs || sm ? "6" : "0"}
										paddingLeft={md || lg || xl ? "4" : "0"}
										paddingRight={"0"}
										marginRight={"0"}
									>
										<Card>
											<CardBody>
												<GetFieldValue<TChildrenArray> name="children">
													{({ value: noOfChildren }) => (
														<GetFieldValue<number> name="noOfAdults">
															{({ value: noOfAdults }) => (
																<GetFieldValue<
																	TTourist[] | undefined
																> name="all_tourists">
																	{({ value: allTourists }) => (
																		<GetFieldValue<
																			number | null
																		> name="primary_contact">
																			{({
																				value: primaryContact,
																				onChange: setPrimaryContact,
																			}) => (
																				<GetFieldValue<
																					TBookingTouristUpdate[] | undefined
																				> name="tourists">
																					{({ value: bookingTourists }) => (
																						<TouristAssignmentInputField
																							name={"members"}
																							tripId={tripId}
																							requiredAdults={noOfAdults}
																							requiredChildren={noOfChildren}
																							allTourists={allTourists}
																							showMisMatchWarning={true}
																							onChange={(value) => {
																								form.change(
																									"members",
																									value as never
																								)

																								const tourists = (value || [])
																									.filter(
																										(m) =>
																											m.member_type ===
																											"tourists"
																									)
																									.map(
																										(m) => m.member as TTourist
																									)

																								const groups = (value || [])
																									.filter(
																										(m) =>
																											m.member_type ===
																											"tourist_groups"
																									)
																									.map(
																										(m) =>
																											m.member as TTouristGroup
																									)

																								const allUniqueTourists = [
																									...tourists,
																									...(groups || []).flatMap(
																										(g) =>
																											(g || []).members.map(
																												(m) => m.tourist
																											)
																									),
																								].filter(
																									(t, index, self) =>
																										self.findIndex(
																											(t2) => t2.id === t.id
																										) === index
																								)

																								form.change(
																									"all_tourists",
																									allUniqueTourists
																								)
																							}}
																							label="Assign Tourists to this Booking"
																							primaryContact={primaryContact}
																							onPrimaryContactChange={(
																								touristId
																							) => {
																								setPrimaryContact(
																									touristId || null
																								)
																							}}
																							bookingTourist={bookingTourists}
																							onBookingTouristChange={(
																								updatedTourists
																							) => {
																								form.change(
																									"tourists",
																									updatedTourists
																								)
																							}}
																						/>
																					)}
																				</GetFieldValue>
																			)}
																		</GetFieldValue>
																	)}
																</GetFieldValue>
															)}
														</GetFieldValue>
													)}
												</GetFieldValue>
											</CardBody>
										</Card>
									</Col>
								</Grid>
							</Box>
							<Inline gap="4">
								<Button type="submit" disabled={submitting}>
									Save
								</Button>
								<Button onClick={onCancel}>Cancel</Button>
							</Inline>
						</Stack>
					</form>
				)}
			</Form>
		</Box>
	)
}

function getDatesFromInterval(
	startDate: string | Date,
	endDate: string | Date
) {
	let date = clone(startDate)
	const dates: Array<string> = []
	while (isBefore(date, endDate, "day")) {
		dates.push(formatDate(date, "YYYY-MM-DD"))
		date = addUnit(date, 1, "day")
	}
	dates.push(formatDate(date, "YYYY-MM-DD"))
	return dates
}
